import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import usePackingListDocuments from '../../hooks/usePackingListDocuments';
import usePackingListCommands from '../../hooks/usePackingListCommands';
import { useOriginalDownload } from '../../hooks/usePackingListFileUrl';
import { unallocatedItems } from '../../lib/packingListAllocation';
import { isSuccess } from '../../lib/packingListRequest';
import { panelHeadSx } from 'shared/config/Styles';
import { clearLastConfirm } from '../../store/packingListSlice';
import PLDocumentList from './items/PLDocumentList';
import ItemsSection from './items/ItemsSection';
import DeleteDocumentDialog from './items/DeleteDocumentDialog';
import PackingListHistoryDrawer from './items/PackingListHistoryDrawer';

/**
 * Order Management 탭의 HBL 작업 영역. 서버에 있는 PL 문서 카드(Draft·Confirmed)와 확정 아이템·배분 패널을 보여준다.
 * 검수는 여기서 하지 않는다. 카드를 누르면 그 PL의 아이템만 거르고, Draft 카드의 Edit은 업로드 다이얼로그에서 이어서 검수한다.
 * Confirmed 카드의 Edit은 같은 다이얼로그를 수정 모드로 연다(onEditDoc).
 * 카드의 X는 Draft·Confirmed 구분 없이 경고를 띄운 뒤 문서를 지운다. 확정본이면 그 PL의 아이템·배분도 함께 사라진다.
 * 카드의 시계는 그 문서의 History Drawer(업로드·확정·수정·배분 이력, 전후 비교)를 연다.
 */
export default function PackingListWorkspace({ hbl, scope, onOpenDoc, onEditDoc }) {
    const dispatch = useDispatch();
    const workspace = usePackingListDocuments(scope);
    const { key, documents: docs, items, allocations, allocationVersion, documentHints, status, error, reload } = workspace;
    const commands = usePackingListCommands(workspace.scope);
    const { download } = useOriginalDownload(commands);
    const lastConfirm = useSelector((s) => s.packingList.lastConfirm);
    const [filterDocId, setFilterDocId] = useState(null);
    /** 삭제 경고 창에 올라간 문서. null이면 닫힘. */
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    /** History Drawer에 올라간 문서. null이면 닫힘. */
    const [historyTarget, setHistoryTarget] = useState(null);
    const unallocatedCount = hbl.containers.length > 1 ? unallocatedItems(items, allocations).length : 0;

    /** 카드를 누르면 그 문서의 아이템만 거르고, 다시 누르면 전체로 돌린다. 검수·수정은 카드의 Edit으로 연다. */
    const handleSelect = (docId) => setFilterDocId(docId);

    /**
     * 배분 패널의 변경 한 번을 서버에 저장하고, 받아들여지면 다시 읽어 화면을 서버 값으로 맞춘다.
     * baseVersion은 그 변경을 정할 때 화면이 보던 allocation_version이다. 동료가 먼저 바꿔 409가 나면 최신 배분을 다시 읽는다.
     * 다시 읽기가 끝난 뒤에 돌려준다. 패널은 그때까지 버튼을 막으므로, 다음 동작은 방금 올라간 버전으로 나가 내 변경끼리 409가 나지 않는다.
     */
    const saveAllocations = async (changes, baseVersion = allocationVersion) => {
        const result = await commands.saveAllocations({ allocationVersion: baseVersion, changes });
        if (isSuccess(result) || result.status === 409) await reload();
        return result;
    };

    /** 경고에 동의한 문서를 지운다. 지운 문서로 아이템을 거르고 있었으면 전체로 돌린다. 실패하면 요청이 띄운 오류로 알리고 창을 닫는다. */
    const confirmDelete = async () => {
        setDeleting(true);
        const result = await commands.deleteDocument({ id: deleteTarget.id, version: deleteTarget.version, allocationVersion });
        setDeleting(false);
        if (isSuccess(result) && filterDocId === deleteTarget.id) setFilterDocId(null);
        setDeleteTarget(null);
        reload();
    };

    const confirmedNotice = lastConfirm?.key === key ? lastConfirm : null;
    const confirmedUnallocated = confirmedNotice && hbl.containers.length > 1 ? unallocatedItems(items.filter((i) => i.packing_list_id === confirmedNotice.documentId), allocations).length : 0;
    /** 재확정 안내. 서버가 재확정 응답으로 준 미배분 전환 수를 보여준다. 남은 미배분 라인은 아래 배분 패널에 그대로 나온다. */
    const editImpact = confirmedNotice?.editImpact ?? null;
    const movedToUnallocated = editImpact ? editImpact.unallocated : 0;
    const noticeText = editImpact
        ? movedToUnallocated
            ? `Re-confirmed. ${movedToUnallocated} line(s) moved to unallocated. Allocate them in the panel below.`
            : 'Re-confirmed.'
        : confirmedUnallocated
          ? `${hbl.containers.length} containers · ${confirmedUnallocated} lines unallocated. Allocate them in the panel below.`
          : 'Confirmed.';
    const noticeWarns = editImpact ? movedToUnallocated > 0 : confirmedUnallocated > 0;

    return (
        <Box sx={{ bgcolor: 'white', borderRadius: '10px', boxShadow: '0px 1px 2px 1px rgba(0, 0, 0, 0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ ...panelHeadSx, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Consignee <b style={{ color: '#003a52', marginLeft: 3 }}>{hbl.consignee_name || '—'}</b></Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>ETA <b style={{ color: '#003a52', marginLeft: 3 }}>{hbl.eta || '—'}</b></Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mr: '2px' }}>{hbl.unit_label}</Typography>
                        {hbl.containers.length === 0 && <Chip size="small" variant="outlined" label="None" sx={{ fontSize: 10, height: 20 }} />}
                        {hbl.containers.map((c) => <Chip key={c.id} size="small" label={`${c.cntr_number} ${c.volume}`.trim()} sx={{ fontSize: 10, height: 20, bgcolor: 'white' }} />)}
                    </Box>
                </Box>
                {unallocatedCount > 0 && <Chip size="small" color="warning" label={`${unallocatedCount} unallocated`} sx={{ fontSize: 10, height: 20 }} />}
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {status === 'error' && (
                    <Alert severity="error" sx={{ fontSize: 12, py: 0 }} action={<Button size="small" onClick={reload} sx={{ fontSize: 11 }}>Retry</Button>}>
                        {error}
                    </Alert>
                )}
                {confirmedNotice && (
                    <Alert severity={noticeWarns ? 'warning' : 'success'} onClose={() => dispatch(clearLastConfirm())} sx={{ fontSize: 12, py: 0 }}>
                        {noticeText}
                    </Alert>
                )}
                {docs.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', py: 3 }}>No packing list on this HBL yet. Use <b>Upload</b> at the top right to add one.</Typography>
                ) : (
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#01354d', mb: 0.75 }}>PL documents</Typography>
                        <PLDocumentList docs={docs} selectedId={filterDocId} onSelect={handleSelect} onDelete={(id) => setDeleteTarget(docs.find((d) => d.id === id))} deleteConfirmed onDownload={download} onEdit={onEditDoc} onEditDraft={(d) => onOpenDoc(d.id)} onHistory={setHistoryTarget} />
                    </Box>
                )}
                <Box sx={{ pt: 1.5, borderTop: '1px solid #e6ebee' }}>
                    <ItemsSection
                        hbl={hbl}
                        items={items}
                        allocations={allocations}
                        allocationVersion={allocationVersion}
                        documentHints={documentHints}
                        filterDocId={filterDocId}
                        onSaveAllocations={saveAllocations}
                    />
                </Box>
            </Box>
            <DeleteDocumentDialog doc={deleteTarget} busy={deleting} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
            <PackingListHistoryDrawer doc={historyTarget} scope={workspace.scope} commands={commands} onClose={() => setHistoryTarget(null)} />
        </Box>
    );
}
